import { FilterPopoverTabs } from '@/Enums/Clients/FilterPopoverTabs.enum';
import { IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { WhitelabelsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/clients';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectClientsFilters } from '@/store/selectors/clients.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import { selectRibeyeAllWhitelabels, selectSharedApiLoading } from '@/store/selectors/sharedApi.selectors';
import { setFilters } from '@/store/slices/clients.slice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEvent, useCallback, useMemo, useState } from 'react';

const FilterPopover = () => {
  const dispatch = useAppDispatch();

  const meRole = useAppSelector(selectMeRole);
  const whitelabels = useAppSelector(selectRibeyeAllWhitelabels);
  const whitelabelsLoading = useAppSelector(selectSharedApiLoading);
  const filters = useAppSelector(selectClientsFilters);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pendingWhitelabels, setPendingWhitelabels] = useState<IWhitelabelControlOption[]>(filters.whitelabels);

  const filtersOpen = useMemo<boolean>(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const currentTab = FilterPopoverTabs.Whitelabels;

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole!,
      selectedWhitelabels: pendingWhitelabels,
      whitelabels,
    });
  }, [meRole, pendingWhitelabels, whitelabels]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const resetPendingValues = useCallback(() => {
    setPendingWhitelabels(filters.whitelabels);
  }, [filters.whitelabels]);

  const updateWhitelabelsValue = useCallback(
    (updatedWhitelabels: IWhitelabelControlOption[] = []) => {
      setPendingWhitelabels(updatedWhitelabels);
    },
    [setPendingWhitelabels],
  );

  const handleOpen = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
  }, [resetPendingValues, setFilterAnchorEl]);

  const handleSubmit = useCallback(() => {
    if (areArraysOfObjectsEqual(filters.whitelabels, pendingWhitelabels)) {
      handleClose();
      return;
    }
    dispatch(setFilters({ whitelabels: pendingWhitelabels }));
    handleClose();
  }, [filters.whitelabels, pendingWhitelabels, dispatch]);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} variant='outlined' />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          aria-label='pacing filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab
            value={FilterPopoverTabs.Whitelabels}
            component={WhitelabelsTabButton}
            {...a11yProps(FilterPopoverTabs.Whitelabels, BASE_TAB_ID)}
          />
        </Tabs>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Whitelabels}>
          <TabSelect
            label='Whitelabels'
            loading={whitelabelsLoading}
            options={options.whitelabel}
            value={pendingWhitelabels}
            onChange={updateWhitelabelsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
